// Explore-stats page: What did Parliament spend its time on? (#213)
// Flagship "Floor time" page. Each `live_minutes` doc carries per-item
// durations; the backend rolls them up into a small set of business
// categories (QT, Bills, Adjournment, Petitions, etc.). This page shows:
//
//   - A 100%-stacked bar per chamber-sitting-day showing category share.
//   - A ranked category-totals bar (overall floor time by category).
//   - A House-vs-Federation Chamber comparison stacked bar.
//   - Scheduled (is_program_item) vs other-time composition per day.
//   - A "Longest single items" list (top 10), linked to /bills/:id where
//     a bill_id is present.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Grid, Box, Stack, Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatStackedBar, HundredPctStacked } from './charts';

export default function TimeOnBusiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('time-on-business');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};

  // Restrict the stacked-bar to the top categories to keep the legend
  // legible. The supporting data table still carries every chamber-day so
  // the long tail is recoverable via CSV.
  const topCategories = (c.categories || []).slice(0, 8);

  // Reshape chamber × category for the comparison stacked bar.
  const chamberStacked = useMemo(() => {
    const rows = {};
    (c.chamber_categories || []).forEach((r) => {
      if (!rows[r.chamber]) rows[r.chamber] = { chamber: r.chamber };
      rows[r.chamber][r.category] = Math.round(r.seconds / 60);  // minutes
    });
    return Object.values(rows);
  }, [c.chamber_categories]);

  return (
    <ExploreStatsPage
      title="What did Parliament spend its time on?"
      description="Floor time by business category for every captured sitting day — Question Time, Bills, Adjournment, Petitions, Committee Reports and the rest. Sourced from the `live_minutes` index (one doc per chamber per sitting day, items array with per-item business durations). Categories are inferred from item headings; long tails fall into 'Other business' until the heading classifier is refined."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="time-on-business"
    >
      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Share of floor time by category — per chamber-sitting-day
              </Typography>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mb: 1 }}>
                Top {topCategories.length} categories by total time across the
                captured period; full breakdown in the table below.
              </Typography>
              <HundredPctStacked
                rows={c.per_day_categories || []}
                categories={topCategories}
                height={Math.max(280, 36 * (c.per_day_categories || []).length + 80)}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Total floor time by category (hours)
              </Typography>
              <StatBar data={c.category_totals || []} xKey="category"
                       yKey="hours" layout="vertical" height={420} />
              {c.median_qt_mins ? (
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}>
                  Median Question Time runs <strong>{c.median_qt_mins} mins</strong>
                  {' '}per sitting day in the captured period.
                </Typography>
              ) : null}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                House vs Federation Chamber — composition (minutes)
              </Typography>
              <StatStackedBar data={chamberStacked} xKey="chamber"
                              keys={topCategories} height={420} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Scheduled vs unscheduled time per day (minutes)
              </Typography>
              <StatStackedBar data={c.scheduled_vs_other || []} xKey="date"
                              keys={['scheduled_mins', 'other_mins']}
                              height={320} />
              <Typography variant="caption" color="text.secondary">
                Scheduled = items where `is_program_item=true`; other = inline
                sub-items (interjections, divisions, etc.).
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Longest single business items
              </Typography>
              <Stack spacing={1}>
                {(c.longest_items || []).map((it, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2,
                                     borderBottom: '1px solid',
                                     borderColor: 'divider', pb: 1 }}>
                    <Box sx={{ minWidth: 80, fontVariantNumeric: 'tabular-nums',
                               fontSize: 12 }}>
                      {it.Date}
                    </Box>
                    <Box sx={{ minWidth: 130, fontSize: 12 }}>
                      {it.Chamber}
                    </Box>
                    <Box sx={{ flex: 1, fontSize: 13 }}>
                      {it.bill_id ? (
                        <Link component={RouterLink}
                              to={`/bills/${it.bill_id}`}
                              underline="hover">{it.Item}</Link>
                      ) : it.Item}
                    </Box>
                    <Box sx={{ minWidth: 90, textAlign: 'right',
                               fontSize: 12 }}>
                      {it['Duration mins']} mins
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </ExploreStatsPage>
  );
}
